import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {catchError, map, mergeMap, switchMap} from 'rxjs/operators';
import * as ResearchKeywordActions from './research-keyword.actions';
import * as ResearchPaperActions from '../research-paper/research-paper.actions';
import {ResearchKeywordService} from './research-keyword.service';
import {ResearchKeywordDTO} from '../../dto/research-keywords';

@Injectable()
export class ResearchKeywordEffects {
  private readonly keywordService: ResearchKeywordService = inject(ResearchKeywordService);
  private readonly actions$: Actions = inject(Actions);

  loadResearchKeywords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchKeywordActions.loadResearchKeywords),
      switchMap((action) =>
        this.keywordService.getKeywords(action.projectId).pipe(
          map((keywords: ResearchKeywordDTO[]) => ResearchKeywordActions.loadResearchKeywordsSuccess({keywords})),
          catchError((error) => of(ResearchKeywordActions.loadResearchKeywordsFailure({error})))
        )
      )
    )
  );

  generateResearchKeyword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchKeywordActions.generateResearchKeyword),
      mergeMap((action) =>
        this.keywordService.generateKeywords(action.projectId).pipe(
          mergeMap((keywords: ResearchKeywordDTO[]) => [
            ResearchKeywordActions.generateResearchKeywordSuccess({
              projectId: action.projectId,
              keywords: keywords
            }),
          ]),
          catchError((error) => of(ResearchKeywordActions.generateResearchKeywordFailure({error})))
        )
      )
    )
  );

  createResearchKeyword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchKeywordActions.createResearchKeyword),
      mergeMap((action) =>
        this.keywordService.createKeyword(action.projectId, action.keyword, action.updateLinkedQueries).pipe(
          mergeMap((keyword: ResearchKeywordDTO) => [
            ResearchKeywordActions.createResearchKeywordSuccess({keyword}),
            ResearchPaperActions.clearPaperStore({projectId: action.projectId})
          ]),
          catchError((error) => of(ResearchKeywordActions.createResearchKeywordFailure({error})))
        )
      )
    )
  );

  deleteResearchKeyword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchKeywordActions.deleteResearchKeyword),
      mergeMap((action) =>
        this.keywordService.deleteKeyword(action.projectId, action.id, action.updateLinkedQueries).pipe(
          mergeMap(() => [
            ResearchKeywordActions.deleteResearchKeywordSuccess({id: action.id}),
            ResearchPaperActions.clearPaperStore({projectId: action.projectId})
          ]),
          catchError((error) => of(ResearchKeywordActions.deleteResearchKeywordFailure({error})))
        )
      )
    )
  );

  retrieveResearchKeyword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchKeywordActions.retrieveResearchKeyword),
      switchMap((action) =>
        this.keywordService.getKeywordById(action.projectId, action.id).pipe(
          map((keyword: ResearchKeywordDTO) => ResearchKeywordActions.retrieveResearchKeywordSuccess({keyword})),
          catchError((error) => of(ResearchKeywordActions.retrieveResearchKeywordFailure({error})))
        )
      )
    )
  );

  updateResearchKeyword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchKeywordActions.updateResearchKeyword),
      mergeMap((action) =>
        this.keywordService.updateKeyword(action.projectId, action.id, action.keyword, action.updateLinkedQueries).pipe(
          mergeMap((keyword: ResearchKeywordDTO) => [
            ResearchKeywordActions.updateResearchKeywordSuccess({keyword}),
            ResearchPaperActions.clearPaperStore({projectId: action.projectId})
          ]),
          catchError((error) => of(ResearchKeywordActions.updateResearchKeywordFailure({error})))
        )
      )
    )
  );

}
